// src/pages/EmployeeStudentForm.jsx
import React, { useState, useEffect } from 'react';
import {
    FiUser,
    FiMail,
    FiPhone,
    FiMapPin,
    FiBook,
    FiFileText,
    FiCreditCard,
    FiCheckCircle,
    FiClock,
    FiAlertCircle,
    FiSave,
    FiX,
    FiEdit2,
    FiEye,
    FiPrinter,
    FiSend,
    FiCalendar,
    FiHash,
    FiKey,
    FiUserCheck,
    FiAward,
    FiInfo,
    FiDollarSign,
    FiPlus,
    FiList,
    FiTrendingUp,
    FiRefreshCw,
    FiChevronDown,
    FiChevronUp,
    FiPackage,
    FiTruck,
    FiCheck,
    FiChevronLeft,
    FiChevronRight
} from 'react-icons/fi';
import { useStudentFormContext } from '../context/StudentFormContext';
import { getCurrentUser } from '../services/authService';
import { studentFormService } from '../services/studentFormService';
import { format, formatDistanceToNow } from 'date-fns';
import { useMemo } from "react";

const EmployeeStudentForm = () => {
    const { myForm, hasForm, createForm, updateMyForm, loading, refreshData, forms, loadMyForm } = useStudentFormContext();
    const currentUser = useMemo(() => getCurrentUser(), []);
    const [isEditing, setIsEditing] = useState(false);
    const [isNewForm, setIsNewForm] = useState(false);
    const [formData, setFormData] = useState({
        student_name: '',
        center_name: '',
        enrolment_number: '',
        roll_number_first_year: '',
        roll_number_second_year: '',
        noc: 'no',
        transaction_id: '',
        amount: '',
    });
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [activeTab, setActiveTab] = useState('form');
    const [expandedForm, setExpandedForm] = useState(null);
    const [notification, setNotification] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingSubmissions, setLoadingSubmissions] = useState(false);
    const [viewFormData, setViewFormData] = useState(null);
    const [loadingViewData, setLoadingViewData] = useState(true);
    const [editingFormId, setEditingFormId] = useState(null);
    const [refreshViewTrigger, setRefreshViewTrigger] = useState(0);

    // Pagination state
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        pageSize: 5,
        hasNext: false,
        hasPrevious: false
    });

    // Load the form data for viewing/editing
    const loadFormData = async () => {
        if (!currentUser?.employee_id) {
            console.log('No employee_id found for loading form data');
            setLoadingViewData(false);
            return;
        }

        setLoadingViewData(true);
        try {
            console.log('Loading form data for employee:', currentUser.employee_id);

            // First check if we have myForm from context
            if (myForm) {
                console.log('Using myForm from context:', myForm);
                setViewFormData(myForm);
                setLoadingViewData(false);
                return;
            }

            // If not in context, fetch it directly from API
            const response = await studentFormService.getMyForm(currentUser.employee_id);
            console.log('Fetched form data response:', response);

            let form = null;

            if (response) {
                if (response.results && Array.isArray(response.results)) {
                    if (response.results.length > 0) {
                        form = response.results[0];
                    }
                } else if (Array.isArray(response)) {
                    if (response.length > 0) {
                        form = response[0];
                    }
                } else if (response.id) {
                    form = response;
                } else if (response.data && response.data.id) {
                    form = response.data;
                }
            }

            if (form) {
                console.log('Setting viewFormData:', form);
                setViewFormData(form);
            } else {
                console.log('No form found for employee');
                setViewFormData(null);
            }
        } catch (error) {
            console.error('Error loading form data:', error);
            if (submissions.length > 0) {
                console.log('Using first submission as view data');
                setViewFormData(submissions[0]);
            } else {
                setViewFormData(null);
            }
        } finally {
            setLoadingViewData(false);
        }
    };

    // Load all submissions for the employee with pagination
    const loadAllSubmissions = async (page = 1) => {
        if (!currentUser?.employee_id) {
            console.log('No employee_id found');
            return;
        }

        setLoadingSubmissions(true);
        try {
            const params = {
                page: page,
                page_size: 5
            };

            console.log('Loading submissions for employee:', currentUser.employee_id, 'page:', page);

            const response = await studentFormService.getMyFormWithParams(currentUser.employee_id, params);
            console.log('All submissions response:', response);

            let allForms = [];
            let paginationData = {
                currentPage: page,
                totalPages: 1,
                totalItems: 0,
                pageSize: 5,
                hasNext: false,
                hasPrevious: false
            };

            if (response && response.results) {
                allForms = response.results;
                paginationData = {
                    currentPage: page,
                    totalPages: Math.ceil(response.count / 5) || 1,
                    totalItems: response.count || 0,
                    pageSize: 5,
                    hasNext: !!response.next,
                    hasPrevious: !!response.previous
                };
            } else if (Array.isArray(response)) {
                allForms = response;
                const total = allForms.length;
                paginationData = {
                    currentPage: page,
                    totalPages: Math.ceil(total / 5) || 1,
                    totalItems: total,
                    pageSize: 5,
                    hasNext: page < Math.ceil(total / 5),
                    hasPrevious: page > 1
                };
                const start = (page - 1) * 5;
                const end = start + 5;
                allForms = allForms.slice(start, end);
            } else if (response) {
                allForms = [response];
                paginationData = {
                    currentPage: page,
                    totalPages: 1,
                    totalItems: 1,
                    pageSize: 5,
                    hasNext: false,
                    hasPrevious: false
                };
            }

            setSubmissions(allForms);
            setPagination(paginationData);
            console.log('Submissions set:', allForms.length, 'forms');
            console.log('Pagination:', paginationData);
        } catch (error) {
            console.error('Error loading submissions:', error);
            try {
                console.log('Falling back to regular getMyForm');
                const fallbackResponse = await studentFormService.getMyForm(currentUser.employee_id);
                console.log('Fallback response:', fallbackResponse);

                let allForms = [];
                if (fallbackResponse && fallbackResponse.results) {
                    allForms = fallbackResponse.results;
                } else if (Array.isArray(fallbackResponse)) {
                    allForms = fallbackResponse;
                } else if (fallbackResponse) {
                    allForms = [fallbackResponse];
                }
                allForms.sort((a, b) => {
                    return new Date(b.created_at) - new Date(a.created_at);
                });

                const total = allForms.length;
                const start = (page - 1) * 5;
                const end = start + 5;
                const slicedForms = allForms.slice(start, end);

                setSubmissions(slicedForms);
                setPagination({
                    currentPage: page,
                    totalPages: Math.ceil(total / 5) || 1,
                    totalItems: total,
                    pageSize: 5,
                    hasNext: page < Math.ceil(total / 5),
                    hasPrevious: page > 1
                });
            } catch (fallbackError) {
                console.error('Fallback error:', fallbackError);
                if (error.status === 404 || error.response?.status === 404) {
                    setSubmissions([]);
                    setPagination({
                        currentPage: 1,
                        totalPages: 1,
                        totalItems: 0,
                        pageSize: 5,
                        hasNext: false,
                        hasPrevious: false
                    });
                }
            }
        } finally {
            setLoadingSubmissions(false);
        }
    };

    // Load data when component mounts
    useEffect(() => {
        if (currentUser?.employee_id) {
            console.log('Component mounted, loading data for employee:', currentUser.employee_id);
            loadAllSubmissions(1);
            setTimeout(() => {
                loadFormData();
            }, 500);
        }
    }, [currentUser?.employee_id]);

    // Reload form data when switching to form tab or when refresh trigger changes
    useEffect(() => {
        if (activeTab === 'form' && currentUser?.employee_id) {
            console.log('Switched to form tab, reloading form data');
            setTimeout(() => {
                loadFormData();
            }, 300);
        }
    }, [activeTab, currentUser?.employee_id, refreshViewTrigger]);

    // Load submissions when forms context changes
    useEffect(() => {
        if (forms && currentUser?.employee_id) {
            loadAllSubmissions(pagination.currentPage);
        }
    }, [forms]);

    // Update view data when myForm changes
    useEffect(() => {
        if (myForm) {
            console.log('myForm updated, updating viewFormData:', myForm);
            setViewFormData(myForm);
        }
    }, [myForm]);

    // Update view data when submissions change
    useEffect(() => {
        if (submissions.length > 0 && !viewFormData) {
            console.log('Setting viewFormData from submissions:', submissions[0]);
            setViewFormData(submissions[0]);
        }
    }, [submissions]);

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            loadAllSubmissions(newPage);
        }
    };

    // Check for updates and show notification
    useEffect(() => {
        if (myForm) {
            const lastUpdated = new Date(myForm.updated_at);
            const now = new Date();
            const diffMinutes = (now - lastUpdated) / (1000 * 60);

            if (diffMinutes < 5 && myForm.amount_verified) {
                setNotification({
                    type: 'success',
                    message: 'HR has updated your form status!',
                    timestamp: myForm.updated_at
                });

                const timer = setTimeout(() => {
                    setNotification(null);
                }, 10000);

                return () => clearTimeout(timer);
            }
        }
    }, [myForm]);

    // Reset form for new submission
    const resetForm = () => {
        setFormData({
            student_name: '',
            center_name: '',
            enrolment_number: '',
            roll_number_first_year: '',
            roll_number_second_year: '',
            noc: 'no',
            transaction_id: '',
            amount: '',
        });
        setErrors({});
        setSuccessMessage('');
    };

    // Load form data for editing
    const loadFormForEditing = (form) => {
        if (form) {
            setFormData({
                student_name: form.student_name || '',
                center_name: form.center_name || '',
                enrolment_number: form.enrolment_number || '',
                roll_number_first_year: form.roll_number_first_year || '',
                roll_number_second_year: form.roll_number_second_year || '',
                noc: form.noc || 'no',
                transaction_id: form.transaction_id || '',
                amount: form.amount || '',
            });
            if (form.id) {
                setEditingFormId(form.id);
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.student_name || !formData.student_name.trim()) {
            newErrors.student_name = 'Student name is required';
        }
        if (!formData.center_name || !formData.center_name.trim()) {
            newErrors.center_name = 'Center name is required';
        }
        if (!formData.enrolment_number || !formData.enrolment_number.trim()) {
            newErrors.enrolment_number = 'Enrolment number is required';
        }
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            newErrors.amount = 'Amount is required and must be greater than 0';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setNotification(null);

        try {
            const submitData = {
                ...formData,
                amount: parseFloat(formData.amount) || 0
            };

            let response;

            if (isNewForm) {
                console.log('Creating new form with data:', submitData);

                // Check if user is logged in before submitting
                const user = getCurrentUser();
                if (!user || !user.id) {
                    setNotification({
                        type: 'error',
                        message: 'Please login again to submit the form.'
                    });
                    setIsSubmitting(false);
                    return;
                }

                response = await createForm(submitData);
                console.log('Create response:', response);

                setSuccessMessage('New form submitted successfully!');
                setNotification({
                    type: 'success',
                    message: 'Your new form has been submitted successfully!'
                });

                resetForm();
                setIsNewForm(false);
                setEditingFormId(null);

                // IMPORTANT: Wait for data to be saved and reload
                await refreshData();
                await loadMyForm();

                // Reload form data after a small delay to ensure DB is updated
                setTimeout(async () => {
                    await loadFormData();
                    await loadAllSubmissions(1);
                    setRefreshViewTrigger(prev => prev + 1);
                }, 500);

                setTimeout(() => {
                    setActiveTab('submissions');
                }, 1500);
            }
            else if (isEditing && editingFormId) {
                console.log('Updating form with ID:', editingFormId);

                response = await studentFormService.updateForm(editingFormId, submitData);
                console.log('Update response:', response);

                setSuccessMessage('Form updated successfully!');
                setNotification({
                    type: 'success',
                    message: 'Your form has been updated successfully!'
                });
                setIsEditing(false);

                await refreshData();
                await loadMyForm();

                setTimeout(async () => {
                    await loadFormData();
                    await loadAllSubmissions(pagination.currentPage);
                    setRefreshViewTrigger(prev => prev + 1);
                }, 500);

                setEditingFormId(null);
            }
            else if (hasForm && myForm && myForm.id) {
                console.log('Updating form using myForm with ID:', myForm.id);
                response = await updateMyForm(submitData);
                setSuccessMessage('Form updated successfully!');
                setNotification({
                    type: 'success',
                    message: 'Your form has been updated successfully!'
                });

                await refreshData();
                await loadMyForm();

                setTimeout(async () => {
                    await loadFormData();
                    await loadAllSubmissions(1);
                    setRefreshViewTrigger(prev => prev + 1);
                }, 500);
            }
            else {
                console.log('Default action - creating new form');

                // Check if user is logged in before submitting
                const user = getCurrentUser();
                if (!user || !user.id) {
                    setNotification({
                        type: 'error',
                        message: 'Please login again to submit the form.'
                    });
                    setIsSubmitting(false);
                    return;
                }

                response = await createForm(submitData);
                setSuccessMessage('Form submitted successfully!');
                setNotification({
                    type: 'success',
                    message: 'Your form has been submitted successfully!'
                });
                resetForm();

                await refreshData();
                await loadMyForm();

                setTimeout(async () => {
                    await loadFormData();
                    await loadAllSubmissions(1);
                    setRefreshViewTrigger(prev => prev + 1);
                }, 500);
            }

            setTimeout(() => {
                setSuccessMessage('');
                setNotification(null);
            }, 5000);
        } catch (err) {
            console.error('Submit error:', err);
            setNotification({
                type: 'error',
                message: err.message || 'Failed to submit form'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const startEditing = (form) => {
        const formToEdit = form || viewFormData || myForm;
        console.log('Starting edit with form:', formToEdit);

        if (formToEdit && formToEdit.id) {
            setIsEditing(true);
            setIsNewForm(false);
            setSuccessMessage('');
            setActiveTab('form');
            loadFormForEditing(formToEdit);
        } else {
            console.error('No valid form to edit');
            setNotification({
                type: 'error',
                message: 'No form found to edit. Please try again.'
            });
        }
    };

    const startNewForm = () => {
        resetForm();
        setIsNewForm(true);
        setIsEditing(false);
        setEditingFormId(null);
        setActiveTab('form');
        setSuccessMessage('');
        setErrors({});
        // Clear view data to show empty form
        setViewFormData(null);
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setIsNewForm(false);
        setEditingFormId(null);
        setErrors({});
        setSuccessMessage('');
        // Reload the original form data
        setTimeout(() => {
            loadFormData();
        }, 100);
    };

    const toggleExpandForm = (formId) => {
        setExpandedForm(expandedForm === formId ? null : formId);
    };

    const refreshSubmissions = () => {
        loadAllSubmissions(pagination.currentPage);
    };

    const getStatusBadge = (form) => {
        const statusClasses = {
            pending: 'bg-red-100 text-red-700 border-red-200',
            verified: 'bg-yellow-100 text-yellow-700 border-yellow-200',
            printed: 'bg-blue-100 text-blue-700 border-blue-200',
            couriered: 'bg-green-100 text-green-700 border-green-200'
        };

        let status = 'pending';
        let icon = <FiClock className="mr-1" />;
        let label = 'Pending';

        if (form.courier_number) {
            status = 'couriered';
            icon = <FiTruck className="mr-1" />;
            label = 'Couriered';
        } else if (form.print_done === 'yes') {
            status = 'printed';
            icon = <FiPrinter className="mr-1" />;
            label = 'Printed';
        } else if (form.amount_verified) {
            status = 'verified';
            icon = <FiCheckCircle className="mr-1" />;
            label = 'Verified';
        }

        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${statusClasses[status]}`}>
                {icon}
                {label}
            </span>
        );
    };

    const getProgress = (form) => {
        let progress = 0;
        if (form.amount_verified) progress += 33;
        if (form.print_done === 'yes') progress += 33;
        if (form.courier_number) progress += 34;
        return progress;
    };

    const getProgressColor = (progress) => {
        if (progress === 100) return 'bg-green-500';
        if (progress >= 66) return 'bg-blue-500';
        if (progress >= 33) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return format(new Date(date), 'dd MMM yyyy, hh:mm a');
    };

    const getFormKey = (form, index) => {
        return form.id || `form-${index}-${form.enrolment_number || Date.now()}`;
    };

    const PaginationControls = () => {
        const { currentPage, totalPages, totalItems, pageSize } = pagination;

        if (totalItems === 0) return null;

        const startItem = (currentPage - 1) * pageSize + 1;
        const endItem = Math.min(currentPage * pageSize, totalItems);

        return (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                    Showing {startItem} to {endItem} of {totalItems} forms
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-lg transition ${currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                    >
                        <FiChevronLeft size={18} />
                    </button>

                    <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (currentPage <= 3) {
                                pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                            } else {
                                pageNum = currentPage - 2 + i;
                            }

                            if (pageNum < 1 || pageNum > totalPages) return null;

                            return (
                                <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`w-10 h-10 rounded-lg transition ${currentPage === pageNum
                                        ? 'bg-indigo-600 text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}

                        {totalPages > 5 && currentPage < totalPages - 2 && (
                            <>
                                <span className="text-gray-400">...</span>
                                <button
                                    onClick={() => handlePageChange(totalPages)}
                                    className="w-10 h-10 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                                >
                                    {totalPages}
                                </button>
                            </>
                        )}
                    </div>

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded-lg transition ${currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                    >
                        <FiChevronRight size={18} />
                    </button>
                </div>
            </div>
        );
    };

    if (loadingViewData) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Loading your form data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="animate-fade-in pb-8">
            {notification && (
                <div className={`mb-6 p-4 rounded-xl border animate-slide-down ${notification.type === 'success'
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : 'bg-red-50 border-red-200 text-red-700'
                    }`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {notification.type === 'success' ? (
                                <FiCheckCircle className="text-xl" />
                            ) : (
                                <FiAlertCircle className="text-xl" />
                            )}
                            <div>
                                <p className="font-medium">{notification.message}</p>
                                {notification.timestamp && (
                                    <p className="text-xs opacity-75">
                                        {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
                                    </p>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={() => setNotification(null)}
                            className="p-1 hover:bg-white/50 rounded-lg transition"
                        >
                            <FiX size={16} />
                        </button>
                    </div>
                </div>
            )}

            <div className="mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
                            <FiBook className="text-indigo-600" />
                            Student Form Dashboard
                        </h1>
                        <p className="text-gray-500 mt-1">Manage and track your student enrollment forms</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => {
                                refreshData();
                                loadMyForm();
                                refreshSubmissions();
                                loadFormData();
                                setRefreshViewTrigger(prev => prev + 1);
                            }}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition text-sm flex items-center gap-2"
                        >
                            <FiRefreshCw size={16} className={loading || loadingSubmissions ? 'animate-spin' : ''} />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-1 mb-6 flex">
                <button
                    onClick={() => {
                        if (viewFormData && !isEditing && !isNewForm) {
                            setActiveTab('form');
                        } else if (viewFormData && (isEditing || isNewForm)) {
                            cancelEditing();
                            setActiveTab('form');
                        } else {
                            setActiveTab('form');
                        }
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 ${activeTab === 'form'
                        ? 'bg-indigo-600 text-white shadow-md'
                        : 'text-gray-600 hover:bg-gray-50'
                        }`}
                >
                    {isNewForm ? (
                        <FiPlus size={18} />
                    ) : isEditing ? (
                        <FiEdit2 size={18} />
                    ) : viewFormData ? (
                        <FiFileText size={18} />
                    ) : (
                        <FiPlus size={18} />
                    )}
                    {isNewForm ? 'New Form' : isEditing ? 'Edit Form' : viewFormData ? 'View Form' : 'New Form'}
                </button>
                <button
                    onClick={() => {
                        setActiveTab('submissions');
                        if (isEditing || isNewForm) {
                            cancelEditing();
                        }
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 ${activeTab === 'submissions'
                        ? 'bg-indigo-600 text-white shadow-md'
                        : 'text-gray-600 hover:bg-gray-50'
                        }`}
                >
                    <FiList size={18} />
                    My Submissions
                    {pagination.totalItems > 0 && (
                        <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${activeTab === 'submissions'
                            ? 'bg-white text-indigo-600'
                            : 'bg-indigo-100 text-indigo-600'
                            }`}>
                            {pagination.totalItems}
                        </span>
                    )}
                </button>
            </div>

            {successMessage && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 animate-slide-down">
                    <div className="flex items-center gap-3">
                        <FiCheckCircle className="text-green-600 text-xl" />
                        <p className="text-green-700">{successMessage}</p>
                    </div>
                </div>
            )}

            {activeTab === 'form' && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                    {isNewForm ? (
                                        <>
                                            <FiPlus className="text-indigo-600" />
                                            New Student Form
                                        </>
                                    ) : isEditing ? (
                                        <>
                                            <FiEdit2 className="text-indigo-600" />
                                            Edit Student Form
                                        </>
                                    ) : viewFormData ? (
                                        <>
                                            <FiFileText className="text-green-600" />
                                            Your Student Form
                                        </>
                                    ) : (
                                        <>
                                            <FiFileText className="text-indigo-600" />
                                            New Student Form
                                        </>
                                    )}
                                </h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    {isNewForm ? 'Fill in the details to submit a new student form' :
                                        isEditing ? 'Update your student enrollment details' :
                                            viewFormData ? 'View your submitted form details' :
                                                'Fill in the details to submit your student form'}
                                </p>
                            </div>
                            {(isEditing || isNewForm) && (
                                <button
                                    onClick={cancelEditing}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-gray-700"
                                    title="Close edit mode"
                                >
                                    <FiX size={22} />
                                </button>
                            )}
                            {!isNewForm && !isEditing && viewFormData && (
                                <button
                                    onClick={startNewForm}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center gap-2 text-sm"
                                >
                                    <FiPlus size={16} />
                                    New Form
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="p-6">
                        {(!viewFormData || isEditing || isNewForm) ? (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Student Name <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                name="student_name"
                                                value={formData.student_name}
                                                onChange={handleInputChange}
                                                className={`w-full pl-10 pr-4 py-2 border ${errors.student_name ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:border-indigo-400`}
                                                placeholder="Enter student name"
                                            />
                                        </div>
                                        {errors.student_name && (
                                            <p className="text-xs text-red-500 mt-1">{errors.student_name}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Center Name <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <FiMapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                name="center_name"
                                                value={formData.center_name}
                                                onChange={handleInputChange}
                                                className={`w-full pl-10 pr-4 py-2 border ${errors.center_name ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:border-indigo-400`}
                                                placeholder="Enter center name"
                                            />
                                        </div>
                                        {errors.center_name && (
                                            <p className="text-xs text-red-500 mt-1">{errors.center_name}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Enrolment Number <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <FiHash className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                name="enrolment_number"
                                                value={formData.enrolment_number}
                                                onChange={handleInputChange}
                                                className={`w-full pl-10 pr-4 py-2 border ${errors.enrolment_number ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:border-indigo-400`}
                                                placeholder="Enter enrolment number"
                                            />
                                        </div>
                                        {errors.enrolment_number && (
                                            <p className="text-xs text-red-500 mt-1">{errors.enrolment_number}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            NOC
                                        </label>
                                        <div className="relative">
                                            <FiCheckCircle className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <select
                                                name="noc"
                                                value={formData.noc}
                                                onChange={handleInputChange}
                                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 appearance-none bg-white"
                                            >
                                                <option value="no">No</option>
                                                <option value="yes">Yes</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Roll Number (1st Year)
                                        </label>
                                        <div className="relative">
                                            <FiKey className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                name="roll_number_first_year"
                                                value={formData.roll_number_first_year}
                                                onChange={handleInputChange}
                                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400"
                                                placeholder="Enter roll number"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Roll Number (2nd Year)
                                        </label>
                                        <div className="relative">
                                            <FiKey className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                name="roll_number_second_year"
                                                value={formData.roll_number_second_year}
                                                onChange={handleInputChange}
                                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400"
                                                placeholder="Enter roll number"
                                            />
                                        </div>
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Transaction ID
                                        </label>
                                        <div className="relative">
                                            <FiCreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                name="transaction_id"
                                                value={formData.transaction_id}
                                                onChange={handleInputChange}
                                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400"
                                                placeholder="Enter transaction ID"
                                            />
                                        </div>
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Amount Paid <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <FiDollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="number"
                                                name="amount"
                                                value={formData.amount}
                                                onChange={handleInputChange}
                                                className={`w-full pl-10 pr-4 py-2 border ${errors.amount ? 'border-red-500' : 'border-gray-200'} rounded-xl focus:outline-none focus:border-indigo-400`}
                                                placeholder="Enter amount paid"
                                                step="0.01"
                                                min="0"
                                            />
                                        </div>
                                        {errors.amount && (
                                            <p className="text-xs text-red-500 mt-1">{errors.amount}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100">
                                    {(isEditing || isNewForm) && (
                                        <button
                                            type="button"
                                            onClick={cancelEditing}
                                            className="px-6 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition flex items-center gap-2"
                                        >
                                            <FiX size={16} />
                                            Cancel
                                        </button>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={isSubmitting || loading}
                                        className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        <FiSave size={16} />
                                        {isSubmitting || loading ? 'Saving...' : isNewForm ? 'Submit New Form' : isEditing ? 'Update Form' : 'Submit Form'}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-gray-50 rounded-xl p-4">
                                        <p className="text-xs text-gray-500 font-medium">Student Name</p>
                                        <p className="text-gray-800 font-medium">{viewFormData?.student_name || '-'}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-4">
                                        <p className="text-xs text-gray-500 font-medium">Center Name</p>
                                        <p className="text-gray-800 font-medium">{viewFormData?.center_name || '-'}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-4">
                                        <p className="text-xs text-gray-500 font-medium">Enrolment Number</p>
                                        <p className="text-gray-800 font-medium">{viewFormData?.enrolment_number || '-'}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-4">
                                        <p className="text-xs text-gray-500 font-medium">NOC</p>
                                        <p className="text-gray-800 font-medium">{viewFormData?.noc === 'yes' ? 'Yes' : 'No'}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-4">
                                        <p className="text-xs text-gray-500 font-medium">Roll Number (1st Year)</p>
                                        <p className="text-gray-800 font-medium">{viewFormData?.roll_number_first_year || '-'}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-4">
                                        <p className="text-xs text-gray-500 font-medium">Roll Number (2nd Year)</p>
                                        <p className="text-gray-800 font-medium">{viewFormData?.roll_number_second_year || '-'}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-4 md:col-span-2">
                                        <p className="text-xs text-gray-500 font-medium">Transaction ID</p>
                                        <p className="text-gray-800 font-medium">{viewFormData?.transaction_id || '-'}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-4 md:col-span-2">
                                        <p className="text-xs text-gray-500 font-medium">Amount Paid</p>
                                        <p className="text-gray-800 font-medium">₹{viewFormData?.amount || '0.00'}</p>
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4 border-t border-gray-100">
                                    <button
                                        onClick={() => startEditing(viewFormData)}
                                        className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center gap-2"
                                    >
                                        <FiEdit2 size={16} />
                                        Edit Form
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'submissions' && (
                <div>
                    <div className="mb-4 flex justify-end">
                        <button
                            onClick={startNewForm}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center gap-2 text-sm"
                        >
                            <FiPlus size={16} />
                            New Form
                        </button>
                    </div>

                    {loadingSubmissions ? (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                            <p className="text-gray-500">Loading your submissions...</p>
                        </div>
                    ) : submissions.length === 0 ? (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FiFileText className="text-gray-400 text-2xl" />
                            </div>
                            <p className="text-gray-500">No submissions yet</p>
                            <p className="text-sm text-gray-400 mt-1">Submit your first student form to get started</p>
                            <button
                                onClick={startNewForm}
                                className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center gap-2"
                            >
                                <FiPlus size={16} />
                                Create New Form
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {submissions.map((form, index) => {
                                const isExpanded = expandedForm === form.id;
                                const progress = getProgress(form);
                                const progressColor = getProgressColor(progress);
                                const isUpdated = form.updated_at !== form.created_at;
                                const formKey = getFormKey(form, index);

                                return (
                                    <div
                                        key={formKey}
                                        className={`bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-md ${isUpdated ? 'border-indigo-300' : ''
                                            } ${isExpanded ? 'shadow-lg' : ''}`}
                                    >
                                        <div
                                            className="p-4 cursor-pointer hover:bg-gray-50 transition"
                                            onClick={() => toggleExpandForm(form.id)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                                                        {form.student_name?.charAt(0) || 'S'}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <p className="font-semibold text-gray-800 truncate">
                                                                {form.student_name}
                                                            </p>
                                                            {isUpdated && (
                                                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700">
                                                                    Updated
                                                                </span>
                                                            )}
                                                            {getStatusBadge(form)}
                                                        </div>
                                                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mt-0.5">
                                                            <span>Enrolment: {form.enrolment_number}</span>
                                                            <span>•</span>
                                                            <span>Amount: ₹{form.amount || '0.00'}</span>
                                                            <span>•</span>
                                                            <span>Submitted: {formatDate(form.created_at)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3 flex-shrink-0">
                                                    <span className="text-xs text-gray-400 hidden sm:inline">
                                                        {formatDistanceToNow(new Date(form.updated_at), { addSuffix: true })}
                                                    </span>
                                                    {isExpanded ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
                                                </div>
                                            </div>

                                            <div className="mt-3">
                                                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                                                    <span>Progress</span>
                                                    <span>{progress}%</span>
                                                </div>
                                                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full transition-all duration-700 ease-out ${progressColor}`}
                                                        style={{ width: `${progress}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div className="border-t border-gray-100 p-4 bg-gray-50 animate-slide-down">
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    <div className="space-y-2">
                                                        <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-2">
                                                            <FiUserCheck className="text-indigo-500" />
                                                            Student Details
                                                        </p>
                                                        <div className="bg-white rounded-lg p-3 space-y-1">
                                                            <p className="text-sm"><span className="text-gray-500">Name:</span> {form.student_name}</p>
                                                            <p className="text-sm"><span className="text-gray-500">Center:</span> {form.center_name}</p>
                                                            <p className="text-sm"><span className="text-gray-500">Enrolment:</span> {form.enrolment_number}</p>
                                                            <p className="text-sm"><span className="text-gray-500">NOC:</span> {form.noc === 'yes' ? 'Yes' : 'No'}</p>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-2">
                                                        <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-2">
                                                            <FiKey className="text-indigo-500" />
                                                            Roll Numbers
                                                        </p>
                                                        <div className="bg-white rounded-lg p-3 space-y-1">
                                                            <p className="text-sm"><span className="text-gray-500">1st Year:</span> {form.roll_number_first_year || 'N/A'}</p>
                                                            <p className="text-sm"><span className="text-gray-500">2nd Year:</span> {form.roll_number_second_year || 'N/A'}</p>
                                                            <p className="text-sm"><span className="text-gray-500">Transaction:</span> {form.transaction_id || 'N/A'}</p>
                                                            <p className="text-sm"><span className="text-gray-500">Amount:</span> ₹{form.amount || '0.00'}</p>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-2">
                                                        <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-2">
                                                            <FiCheckCircle className="text-indigo-500" />
                                                            HR Status
                                                        </p>
                                                        <div className="bg-white rounded-lg p-3 space-y-1">
                                                            <p className="text-sm flex items-center gap-2">
                                                                <span className="text-gray-500">Verified:</span>
                                                                {form.amount_verified ? (
                                                                    <FiCheckCircle className="text-green-500" />
                                                                ) : (
                                                                    <FiClock className="text-yellow-500" />
                                                                )}
                                                                <span>{form.amount_verified ? 'Yes' : 'Pending'}</span>
                                                            </p>
                                                            <p className="text-sm"><span className="text-gray-500">Print Date:</span> {form.printing_date ? format(new Date(form.printing_date), 'dd MMM yyyy') : 'N/A'}</p>
                                                            <p className="text-sm"><span className="text-gray-500">Print Status:</span>
                                                                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium ${form.print_done === 'yes'
                                                                    ? 'bg-blue-100 text-blue-700'
                                                                    : 'bg-gray-100 text-gray-600'
                                                                    }`}>
                                                                    {form.print_done || 'pending'}
                                                                </span>
                                                            </p>
                                                            <p className="text-sm"><span className="text-gray-500">Courier:</span>
                                                                {form.courier_number ? (
                                                                    <span className="ml-1 text-green-600 font-medium flex items-center gap-1">
                                                                        <FiTruck size={14} />
                                                                        {form.courier_number}
                                                                    </span>
                                                                ) : 'N/A'}
                                                            </p>
                                                            {form.remark && (
                                                                <p className="text-sm"><span className="text-gray-500">Remark:</span> {form.remark}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="mt-4 flex flex-wrap gap-2 pt-3 border-t border-gray-200">
                                                    <button
                                                        onClick={() => {
                                                            setExpandedForm(null);
                                                            startEditing(form);
                                                        }}
                                                        className="px-3 py-1.5 text-sm bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition flex items-center gap-1"
                                                    >
                                                        <FiEdit2 size={14} /> Edit Form
                                                    </button>
                                                    <button
                                                        onClick={() => setShowPreview(!showPreview)}
                                                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center gap-1"
                                                    >
                                                        <FiEye size={14} /> Preview
                                                    </button>
                                                    <button
                                                        onClick={startNewForm}
                                                        className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition flex items-center gap-1"
                                                    >
                                                        <FiPlus size={14} /> New Form
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            <PaginationControls />
                        </div>
                    )}
                </div>
            )}

            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                    <FiInfo className="text-blue-600 text-xl mt-0.5" />
                    <div>
                        <h4 className="font-medium text-blue-800">Student Form Tracking</h4>
                        <p className="text-sm text-blue-700 mt-1">
                            • Submit your student enrollment details and track the status in real-time.
                            <br />
                            • HR will verify the form and update the status (Pending → Verified → Printed → Couriered).
                            <br />
                            • You can submit multiple forms using the "New Form" button.
                            <br />
                            • All your submissions are shown in the "My Submissions" tab with pagination (5 forms per page).
                            <br />
                            • Click on any submission to view detailed HR status or edit the form.
                            <br />
                            • Navigate through pages using the pagination controls at the bottom.
                        </p>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes pulse-subtle {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.7; }
                }
                .animate-pulse-subtle {
                    animation: pulse-subtle 2s ease-in-out infinite;
                }
                @keyframes slide-down {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-slide-down {
                    animation: slide-down 0.3s ease-out;
                }
                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .animate-fade-in {
                    animation: fade-in 0.5s ease-out;
                }
            `}</style>
        </div>
    );
};

export default EmployeeStudentForm;